import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from "next";
import React, { useCallback, useMemo } from "react";
import { AppSettings } from "../../../lib/get-app-settings";
import {
  ConversationTab,
  CustomerPortalContextProvider,
} from "../../../components/customer-portal/customer-portal-context";
import ConversationsTabs from "../../../components/customer-portal/conversations-list/conversations-tabs";
import loadCustomer from "../../../lib/load-customer";
import Customer, { TCustomer } from "../../../models/customer";
import Conversation, { TConversation } from "../../../models/conversation";
import loadConversations, {
  TLoadConversationsPayload,
} from "../../../lib/load-conversations";
import createVisitor from "../../../lib/create-visitor";
import cookieString from "../../../lib/cookie-string";
import type { THelpCenterLightLayout } from "../../../lib/load-company-settings";
import LightLayout from "../../../components/layout.light";
import styles from "../../../styles/customer-portal.module.css";
import { TLayoutSidebarLightLink } from "../../../components/layout-sidebar.light";
import { useRouter } from "next/router";
import getBaseUrl from "../../../lib/get-base-url";
import t from "../../../texts/t";

const TicketsLightPage: NextPage<TTicketsLightPageProps> = ({
  appSettings,
  customerData,
  customerConversationsData,
  customerConversationsTotal,
  accountConversationsData,
  accountConversationsTotal,
}) => {
  const customer = useMemo(
    () => customerData && new Customer(customerData),
    [customerData]
  );

  const initial = useMemo(
    () => ({
      customerConversations: customerConversationsData.map(
        (c) => new Conversation(c)
      ),
      customerTotal: customerConversationsTotal,
      accountConversations: accountConversationsData.map(
        (c) => new Conversation(c)
      ),
      accountTotal: accountConversationsTotal,
    }),
    [
      accountConversationsData,
      accountConversationsTotal,
      customerConversationsData,
      customerConversationsTotal,
    ]
  );

  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);
  const router = useRouter();
  const switchTab = useCallback(
    (tab: string) => {
      const { companySlug, ...oldQuery } = router.query;

      router.replace({
        pathname: `${baseUrl}/customer-portal`,
        query: { ...oldQuery, tab },
      });
    },
    [baseUrl, router]
  );

  const tabsLinks = useMemo<TLayoutSidebarLightLink[]>(() => {
    return [
      {
        title: t("My Tickets", appSettings.language),
        url: "",
        onClick: (e) => {
          e.preventDefault();
          switchTab(ConversationTab.customer);
        },
        selected: router.query.tab !== ConversationTab.account,
      },
      {
        title: t("Company tickets", appSettings.language),
        url: "",
        onClick: (e) => {
          e.preventDefault();
          switchTab(ConversationTab.account);
        },
        selected: router.query.tab === ConversationTab.account,
      },
    ];
  }, [switchTab, appSettings.language]);

  const modifyiedAppSettings = useMemo<AppSettings>(() => {
    return {
      ...appSettings,
      secondaryColorLight: "#ffffff",
      secondaryColorDark: "#1b1c1d",
    };
  }, [appSettings]);

  return (
    <CustomerPortalContextProvider
      customer={customer}
      appSettings={modifyiedAppSettings}
      initial={initial}
    >
      <LightLayout
        className={styles.customerPortalLayout}
        appSettings={modifyiedAppSettings}
        pageTitle={appSettings.supportPortalTitle}
        pageDescription={appSettings.supportPortalDescription}
        links={tabsLinks}
        isCustomPageTitle
        contentWidth={892}
        hideFooter
      >
        <h1 className={styles.customerPortalLightTitle}>
          {appSettings.supportPortalTitle}
        </h1>
        <p className={styles.customerPortalLightSubtitle}>
          {appSettings.supportPortalDescription}
        </p>
        <ConversationsTabs appSettings={modifyiedAppSettings} hideTabs />
      </LightLayout>
    </CustomerPortalContextProvider>
  );
};

export default TicketsLightPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  appSettings: AppSettings & { layout: THelpCenterLightLayout }
): Promise<ReturnType<GetServerSideProps<TTicketsLightPageProps>>> => {
  const { appId } = appSettings;

  const cookieIdKey = `atlasIdentity${appId}`;
  const currentCustomerId = context.req.cookies[cookieIdKey];

  let filters;
  let sorts;
  try {
    filters =
      context.query.filters && JSON.parse(context.query.filters as string);
    sorts = context.query.sorts && JSON.parse(context.query.sorts as string);
  } catch (error) {
    console.error(error);
  }

  const customerTabFilters: TLoadConversationsPayload = {
    sort: sorts?.customer,
    filters: filters?.customer,
  };
  const accountTabFilters: TLoadConversationsPayload = {
    sort: sorts?.account,
    filters: filters?.account,
  };

  const [
    customerData,
    { data: customerConversationsData, total: customerConversationsTotal },
    { data: accountConversationsData, total: accountConversationsTotal },
  ] = await Promise.all([
    currentCustomerId
      ? loadCustomer(appId, currentCustomerId).catch((error) => {
          console.error(error);
          return createVisitor(appId);
        })
      : createVisitor(appId),
    currentCustomerId
      ? loadConversations(appId, context, false, customerTabFilters).catch(
          handleConversationsError
        )
      : emptyConversationsResponse,
    currentCustomerId
      ? loadConversations(appId, context, true, accountTabFilters).catch(
          handleConversationsError
        )
      : emptyConversationsResponse,
  ]);

  if (!currentCustomerId && customerData) {
    context.res.setHeader(
      "Set-Cookie",
      cookieString(cookieIdKey, customerData.id, context, appSettings)
    );
  }

  return {
    props: {
      appSettings,
      customerData,
      customerConversationsData,
      customerConversationsTotal,
      accountConversationsData,
      accountConversationsTotal,
    },
  };
};

const emptyConversationsResponse: {
  data: TConversation[];
  total: 0;
} = { data: [], total: 0 };

function handleConversationsError(error: any) {
  console.error(error);
  return emptyConversationsResponse;
}

export type TTicketsLightPageProps = {
  appSettings: AppSettings;
  customerData: TCustomer | null;
  customerConversationsData: TConversation[];
  customerConversationsTotal: number;
  accountConversationsData: TConversation[];
  accountConversationsTotal: number;
};
